import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import type {
	RecentActivityPreview_activity$data,
	RecentActivityPreview_activity$key,
} from './__generated__/RecentActivityPreview_activity.graphql';
import {RecentActivityEntry, type ActivityBadgeVariant} from './RecentActivityEntry';
import {htmlToPreviewText} from '../utils/htmlToPreviewText';
import {resolveMentions} from '../utils/resolveMentions';

const PreviewFragment = graphql`
	fragment RecentActivityPreview_activity on ActivityEvent {
		__typename
		... on MessageActivity {
			id
			createdAt
			body
			authorType
			direction
			author {
				fullName
			}
		}
		... on InternalCommentActivity {
			id
			createdAt
			body
			author {
				fullName
			}
			mentionedUsers {
				id
				fullName
			}
		}
		... on EventActivity {
			id
			createdAt
			eventType
			actor {
				fullName
			}
			oldValue
			newValue
		}
		... on AttachmentActivity {
			id
			createdAt
			filename
		}
	}
`;

interface Props {
	entries: readonly RecentActivityPreview_activity$key[];
	onViewAll: () => void;
}

interface BadgeStyle {
	label: string;
	variant: ActivityBadgeVariant;
}

const Heading = styled.h3`
	margin: 0 0 8px;
	${p => p.theme.sys.font?.subheading?.['1']}
	letter-spacing: 0.15px;
	color: #0f172a;
`;

const EmptyState = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.body?.['2']}
	color: #64748b;
`;

const Divider = styled.div`
	border-top: 1px solid #e2e8f0;
`;

export function RecentActivityPreview({entries, onViewAll}: Props) {
	// The backend emits `timeline` sorted ascending (oldest-first), so take the
	// last three entries and reverse them to show the three most recent first.
	const recentThree = entries.slice(-3).reverse();

	return (
		<div>
			<Heading>Recent activity</Heading>
			{recentThree.length === 0 ? (
				<EmptyState>No activity yet.</EmptyState>
			) : (
				<div>
					{recentThree.map((entry, i) => (
						<PreviewEntry key={i} entry={entry} showDivider={i > 0} />
					))}
				</div>
			)}
			<Button emphasis={EMPHASIS.LOW} onPress={onViewAll}>
				View full activity history →
			</Button>
		</div>
	);
}

function PreviewEntry({entry, showDivider}: {entry: RecentActivityPreview_activity$key; showDivider: boolean}) {
	const data = useFragment(PreviewFragment, entry);
	if (data.__typename === '%other') return null;
	const badge = getTypeBadge(data.__typename);
	const message = getMessageText(data);
	const actorName = getActorName(data);
	const avatarInitial = actorName.charAt(0).toUpperCase();

	return (
		<>
			{showDivider && <Divider data-testid="activity-divider" />}
			<RecentActivityEntry
				avatarInitial={avatarInitial}
				typeBadge={badge}
				message={message}
				actorName={actorName}
				timestamp={data.createdAt as string}
			/>
		</>
	);
}

function getTypeBadge(typename: string): BadgeStyle {
	switch (typename) {
		case 'MessageActivity':
			return {label: 'Email', variant: 'email'};
		case 'InternalCommentActivity':
			return {label: 'Comment', variant: 'comment'};
		case 'EventActivity':
			return {label: 'Status change', variant: 'status'};
		case 'AttachmentActivity':
			return {label: 'System', variant: 'system'};
		default:
			return {label: 'Activity', variant: 'system'};
	}
}

type FragmentData = Exclude<RecentActivityPreview_activity$data, {__typename: '%other'}>;

function getMessageText(entry: FragmentData): string {
	switch (entry.__typename) {
		case 'MessageActivity':
			return htmlToPreviewText(entry.body ?? '');
		case 'InternalCommentActivity': {
			const names = Object.fromEntries((entry.mentionedUsers ?? []).map(u => [u.id, u.fullName ?? 'Deleted user']));
			return htmlToPreviewText(resolveMentions(entry.body ?? '', names));
		}
		case 'EventActivity': {
			if (entry.oldValue && entry.newValue) {
				return `${entry.eventType}: ${entry.oldValue} → ${entry.newValue}`;
			}
			return entry.eventType ?? 'Event';
		}
		case 'AttachmentActivity':
			return `File uploaded: ${entry.filename ?? 'attachment'}`;
		default:
			return '';
	}
}

function getActorName(entry: FragmentData): string {
	switch (entry.__typename) {
		case 'MessageActivity':
			return entry.author
				? entry.author.fullName ?? 'Deleted user'
				: entry.authorType === 'agent'
				? 'Agent'
				: 'Requester';
		case 'InternalCommentActivity':
			return entry.author ? entry.author.fullName ?? 'Deleted user' : 'Agent';
		case 'EventActivity':
			return entry.actor ? entry.actor.fullName ?? 'Deleted user' : 'System';
		case 'AttachmentActivity':
			return 'System';
		default:
			return 'Unknown';
	}
}
